import { floorManagerAdjustedMinutes } from './floor-manager-thresholds.js';
const STATUS_LABELS = {
    online: 'Online',
    busy: 'Busy',
    away: 'Away',
    missing: 'Missing',
    offline: 'Offline',
    inactive: 'Inactive',
};
const SCHEDULE_LABELS = {
    'on-shift': 'On shift',
    'off-schedule': 'Off schedule',
    excused: 'Excused',
};
export function getPresenceStatusLabel(status) {
    return STATUS_LABELS[status];
}
export function getScheduleLabel(state) {
    return SCHEDULE_LABELS[state];
}
// ── Derived predicates ───────────────────────────────────────────────
/**
 * The tester is currently working a job. Derived purely from `activeJob` —
 * no stored `availability` value carries this meaning anymore.
 */
export function isBusy(tester) {
    return tester.activeJob !== null;
}
/**
 * The tester hasn't shown any activity for a long time. Surfaces them in
 * the "Inactive" sidebar section so they aren't conflated with testers who
 * just stepped away today.
 */
export function isInactive(tester, ctx) {
    const lastActive = new Date(tester.lastActiveAt).getTime();
    const cutoff = ctx.nowMs - ctx.inactiveThresholdDays * 24 * 60 * 60 * 1000;
    return lastActive < cutoff;
}
/**
 * The tester is scheduled to be on-shift right now, but they aren't
 * actively working a job and haven't shown activity in the missing
 * window — they should be here, but they aren't. Renders in the
 * "Missing" sidebar section as an overlay over Away/Offline.
 */
export function isMissing(tester, ctx) {
    if (tester.scheduleState !== 'on-shift')
        return false;
    if (tester.activeJob)
        return false;
    const lastActive = new Date(tester.lastActiveAt).getTime();
    const thresholdMinutes = floorManagerAdjustedMinutes(ctx.missingThresholdMinutes, tester.isFloorManager);
    const cutoff = ctx.nowMs - thresholdMinutes * 60 * 1000;
    return lastActive < cutoff;
}
/**
 * The tester isn't scheduled, but they're around anyway — worth rendering
 * with a dimmer treatment so the roster visually prioritizes on-shift testers.
 *
 * #4427 — `invisible` is functionally "not around" (hidden from presence,
 * claim-blocked), so it's treated the same as `offline` for this signal.
 */
export function isOffScheduleAndPresent(tester) {
    if (tester.availability === 'offline' || tester.availability === 'invisible')
        return false;
    return tester.scheduleState === 'off-schedule';
}
// ── Bucket assignment ────────────────────────────────────────────────
/**
 * Assigns a tester to exactly one of the six sidebar buckets.
 *
 * Priority order (first match wins):
 *   1. `busy`     — active job overrides everything else (you're working)
 *   2. `away`*    — *only when the stored availability is `on_break`*: the
 *                   sweep wrote that precisely because an approved-break
 *                   window covers "now", so we trust it over the
 *                   `missing`/`inactive` overlays. The user-visible bucket
 *                   is `away` (#3667 collapsed the separate "On break"
 *                   bucket into "Away") but the priority slot belongs here.
 *   3. `inactive` — past the long-staleness window
 *   4. `missing`  — on-shift but absent (overrides the underlying Away/Offline)
 *   5. `away`     — stored availability (manual self-Away)
 *   6. `offline`  — stored availability
 *   7. `online`   — fallback (stored availability)
 *
 * The derived buckets sit above the stored ones in priority because the
 * sidebar wants the most informative label — a busy tester is "Busy", not
 * "Online and busy"; a long-stale offline tester is "Inactive", not "Offline".
 * `on_break` keeps the priority slot it had when it was its own bucket
 * (#3774): the sweep set it deliberately, so it should not be overwritten
 * by the staleness overlays. Only the *label* it emits changes.
 */
export function getPresenceStatus(tester, ctx) {
    if (isBusy(tester))
        return 'busy';
    if (tester.availability === 'on_break')
        return 'away';
    // #4427 — `invisible` is admin-only. The presence handler filters it for
    // every viewer except the requesting user (who needs their own YOU card).
    // Bucket it as `offline` here so the rare reads that DO see it (e.g. the
    // self-row feeding TeamMemberCard) get a valid PresenceStatus instead of
    // an exception. The YOU card distinguishes "invisible" from "offline" by
    // reading `tester.availability` directly — see TeamMemberCard.
    if (tester.availability === 'invisible')
        return 'offline';
    if (isInactive(tester, ctx))
        return 'inactive';
    if (isMissing(tester, ctx))
        return 'missing';
    return tester.availability;
}
// ── Time helpers ─────────────────────────────────────────────────────
/**
 * Human-readable elapsed duration since an ISO timestamp.
 * Used by the presence bar to show "how long has this tester been on this job".
 */
export function formatElapsed(claimedAt) {
    const elapsedMs = Date.now() - new Date(claimedAt).getTime();
    const minutes = Math.floor(elapsedMs / 60_000);
    if (minutes < 1)
        return 'just now';
    if (minutes < 60)
        return `${minutes}m elapsed`;
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return remainder === 0 ? `${hours}h elapsed` : `${hours}h ${remainder}m elapsed`;
}
//# sourceMappingURL=presence-derivations.js.map